import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import { Badge, Button, Checkbox, Message } from '@/platform/ui';
import { useForm, usePage } from '@inertiajs/react';
import { groupByQuestion, questionnaireSubtitle } from './questionnaire';
import SidePanel, { closePanel } from './SidePanel';

const QuestionnaireEditPanel = () => {
    const { employee, questionnaire } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();

    const questions = groupByQuestion(questionnaire.questions ?? []);
    const baseUrl = `/customers/${employee.customer_id}/employees/${employee.id}/questionnaires`;
    const close = () => closePanel(baseUrl);

    const { data, setData, patch, processing, errors } = useForm({
        answers: (questionnaire.questions ?? [])
            .filter(row => row.answer === true)
            .map(row => row.id),
    });

    const toggle = (id, checked) =>
        setData(
            'answers',
            checked
                ? [...data.answers, id]
                : data.answers.filter(answer => answer !== id)
        );

    const submit = () =>
        confirm({
            title: 'Vragenlijst afronden?',
            description:
                'De vragenlijst wordt afgerond als "Voltooid per telefoon", met toestemming van de werknemer. Dit kun je niet ongedaan maken.',
            confirmLabel: 'Afronden',
            onConfirm: closeConfirmation => {
                setLoading(true);

                patch(`${baseUrl}/${questionnaire.id}`, {
                    replace: true,
                    onFinish: closeConfirmation,
                });
            },
        });

    return (
        <SidePanel
            title={
                <>
                    Vragenlijst invullen
                    <Badge color={questionnaire.state_color}>
                        {questionnaire.state_label}
                    </Badge>
                </>
            }
            subtitle={questionnaireSubtitle(employee, questionnaire)}
            onClose={close}
            footer={
                <>
                    <p className='text-sm text-gray-600'>
                        Vink per vraag aan wat van toepassing is. Niet
                        aangevinkt betekent nee.
                    </p>
                    {errors.answers && (
                        <p className='text-sm text-red-600'>{errors.answers}</p>
                    )}
                    <div className='flex justify-end gap-2'>
                        <Button type='button' onClick={close}>
                            Annuleren
                        </Button>
                        <Button
                            type='button'
                            variant='primary'
                            loading={processing}
                            disabled={questions.length === 0}
                            onClick={submit}
                        >
                            Vragenlijst afronden
                        </Button>
                    </div>
                </>
            }
        >
            {questions.length === 0 ? (
                <Message variant='warning'>
                    Deze vragenlijst heeft geen vragen en kan niet worden
                    ingevuld.
                </Message>
            ) : (
                <ol className='flex flex-col gap-6'>
                    {questions.map((question, index) => (
                        <li key={question.id}>
                            <fieldset>
                                <legend className='mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900'>
                                    {index + 1}. {question.question}
                                    {question.options.some(
                                        option => option.autofilled
                                    ) && (
                                        <Badge color='gray' size='sm'>
                                            Automatisch ingevuld
                                        </Badge>
                                    )}
                                </legend>
                                <div className='flex flex-col gap-2'>
                                    {question.options.map(option => (
                                        <Checkbox
                                            key={option.id}
                                            label={option.option}
                                            checked={data.answers.includes(
                                                option.id
                                            )}
                                            onChange={checked =>
                                                toggle(option.id, checked)
                                            }
                                        />
                                    ))}
                                </div>
                            </fieldset>
                        </li>
                    ))}
                </ol>
            )}
        </SidePanel>
    );
};

export default QuestionnaireEditPanel;
